import { useEffect, useState } from "react";
import { Eye, Send } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { http, money, STATUS, API, errMsg } from "@/lib/api";
import { PageTitle } from "@/components/admin/AdminLayout";

const StatusSelect = ({ o, onChange, busy }) => (
  <Select value={o.status} onValueChange={(v) => onChange(o, v)} disabled={busy}>
    <SelectTrigger className={`h-8 w-48 text-xs border ${STATUS[o.status].cls}`} data-testid={`admin-order-status-select-${o.id}`}><SelectValue placeholder="..." />{busy && <span className="ml-1 animate-pulse">…</span>}</SelectTrigger>
    <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
  </Select>
);

const DELIVERY = {
  aguardando: ["Aguardando envio", "border-slate-500/40 text-slate-300"],
  enviando: ["Enviando...", "border-cyan-500/40 text-cyan-300"],
  entregue: ["Enviado ao servidor", "border-emerald-500/40 text-emerald-300"],
  erro: ["Falha no envio", "border-rose-500/40 text-rose-300"],
  sem_comandos: ["Sem comandos", "border-amber-500/40 text-amber-300"],
};

const DeliveryBadge = ({ o }) => {
  const [l, c] = DELIVERY[o.delivery?.status] || DELIVERY.aguardando;
  return <span className={`text-[10px] font-semibold px-2 py-1 rounded border ${c}`} data-testid={`admin-order-delivery-status-${o.id}`}>{l}</span>;
};

const OrderDetail = ({ o, onDeliver }) => (
  <div className="space-y-4 text-sm">
    <div className="grid grid-cols-2 gap-3">
      <div><p className="text-xs text-slate-500">Cliente</p><p>{o.customer_name}</p></div>
      <div><p className="text-xs text-slate-500">E-mail</p><p className="break-all">{o.email}</p></div>
      <div><p className="text-xs text-slate-500">Nick no jogo</p><p className="font-mono">{o.nickname}</p></div>
      <div><p className="text-xs text-slate-500">Pagamento</p><p>{o.payment_method === "pix" ? "Pix" : "MB Way"} · <span className="font-mono">{money(o.total, o.currency)}</span></p></div>
    </div>
    {o.notes && <div><p className="text-xs text-slate-500">Observações</p><p>{o.notes}</p></div>}
    <div className="rounded-xl bg-white/5 p-3">{o.items.map((i) => <p key={i.product_id}>{i.quantity}× {i.name} — <span className="font-mono">{money(i.unit_price * i.quantity, o.currency)}</span></p>)}</div>
    <div>
      <p className="text-xs text-slate-500 mb-2">Entrega no Minecraft</p>
      <DeliveryBadge o={o} />
      {o.delivery?.error && <p className="text-rose-300 text-xs mt-2" data-testid="admin-order-delivery-error">{o.delivery.error}</p>}
      {o.delivery?.log?.length > 0 && (
        <div className="mt-2 rounded-xl bg-black/40 p-3 font-mono text-xs space-y-1" data-testid="admin-order-delivery-log">
          {o.delivery.log.map((l, i) => <p key={i}><span className="text-emerald-300">/{l.command}</span>{l.response && <span className="text-slate-500"> → {l.response}</span>}</p>)}
        </div>
      )}
      <button onClick={() => onDeliver(o)} className="mt-3 h-9 px-4 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-semibold inline-flex items-center gap-2" data-testid="admin-order-deliver-button">
        <Send className="w-3.5 h-3.5" /> {o.delivery?.status === "entregue" ? "Reenviar itens" : "Enviar itens agora"}
      </button>
    </div>
    <div>
      <p className="text-xs text-slate-500 mb-2">Comprovativo</p>
      {!o.proof ? <p className="text-slate-500" data-testid="admin-order-no-proof">Nenhum comprovativo enviado.</p> : o.proof.endsWith(".pdf") ? (
        <a href={`${API}/files/${o.proof}`} target="_blank" rel="noreferrer" className="text-violet-400" data-testid="admin-order-proof-link">Abrir PDF</a>
      ) : (
        <a href={`${API}/files/${o.proof}`} target="_blank" rel="noreferrer"><img src={`${API}/files/${o.proof}`} alt="Comprovativo" className="rounded-xl max-h-80 border border-white/10" data-testid="admin-order-proof-image" /></a>
      )}
    </div>
  </div>
);

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const load = () => http.get("/admin/orders", { params: filter === "all" ? {} : { status: filter } }).then((r) => setOrders(r.data));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const change = async (o, status) => {
    setBusyId(o.id);
    try {
      const r = await http.patch(`/admin/orders/${o.id}`, { status });
      const d = r.data.delivery?.status;
      if (status === "pago" && d === "entregue") toast.success(`Pedido ${o.code}: itens enviados para ${o.nickname}!`);
      else if (status === "pago" && d === "erro") toast.error(`Pago, mas falhou o envio: ${r.data.delivery.error}`);
      else toast.success(`Pedido ${o.code}: ${STATUS[r.data.status].label}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusyId(null); }
  };
  const deliver = async (o) => {
    try {
      const r = await http.post(`/admin/orders/${o.id}/deliver`);
      setView(r.data);
      if (r.data.delivery.status === "entregue") toast.success(`Itens enviados para ${o.nickname}!`);
      else toast.error(r.data.delivery.error || "Falha no envio");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div>
      <PageTitle title="Pedidos" sub="Confira os comprovativos e atualize o estado de cada pedido" />
      <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
        {[["all", "Todos"], ...Object.entries(STATUS).map(([k, v]) => [k, v.label])].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`admin-orders-filter-${k}`}
            className={`shrink-0 h-9 px-4 rounded-full text-xs font-medium border transition-colors ${filter === k ? "bg-violet-600 border-transparent text-white" : "border-white/10 text-slate-400 hover:text-white"}`}>{l}</button>
        ))}
      </div>
      <div className="rounded-2xl bg-[#111827] border border-white/10 divide-y divide-white/5" data-testid="admin-orders-list">
        {orders.length === 0 && <p className="p-6 text-sm text-slate-500" data-testid="admin-orders-empty">Nenhum pedido.</p>}
        {orders.map((o) => (
          <div key={o.id} className="flex flex-wrap items-center gap-4 p-4 text-sm" data-testid={`admin-order-${o.id}`}>
            <span className="font-mono font-bold w-20">{o.code}</span>
            <div className="flex-1 min-w-[140px]"><p className="truncate">{o.customer_name}</p><p className="text-xs text-slate-500 font-mono truncate">{o.nickname}</p></div>
            <span className={`text-[10px] font-mono px-2 py-1 rounded border ${o.payment_method === "pix" ? "border-emerald-500/40 text-emerald-300" : "border-rose-500/40 text-rose-300"}`}>{o.payment_method === "pix" ? "PIX" : "MB WAY"}</span>
            <span className="font-mono w-24 text-right">{money(o.total, o.currency)}</span>
            <DeliveryBadge o={o} />
            <StatusSelect o={o} onChange={change} busy={busyId === o.id} />
            <button onClick={() => setView(o)} className="p-2 text-slate-400 hover:text-white relative" data-testid={`admin-order-view-${o.id}`}>
              <Eye className="w-4 h-4" />{o.proof && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-cyan-400" />}
            </button>
          </div>
        ))}
      </div>
      <Dialog open={!!view} onOpenChange={(v) => !v && setView(null)}>
        <DialogContent className="bg-[#111827] border-white/10 text-slate-100 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading font-mono">Pedido {view?.code}</DialogTitle></DialogHeader>
          {view && <OrderDetail o={view} onDeliver={deliver} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
